import { useEffect, useState } from "react";
import { useAuth, useUser } from "@clerk/react";
import { Navigate, useLocation } from "react-router-dom";

import { createOnboardingApi } from "@/lib/api/onboarding";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const location = useLocation();

  // DB fallback: if Clerk metadata is missing, check the backend profile
  const [dbOnboardingChecked, setDbOnboardingChecked] = useState(false);
  const [dbOnboardingComplete, setDbOnboardingComplete] = useState(false);

  const clerkComplete = user?.publicMetadata?.onboardingComplete === true;

  useEffect(() => {
    if (!isSignedIn || clerkComplete || dbOnboardingChecked) return;

    const api = createOnboardingApi({ getToken });
    api
      .getProfile()
      .then((profile) => {
        setDbOnboardingComplete(profile.completed_at !== null);
      })
      .catch(() => {
        // If profile fetch fails, don't block — treat as incomplete
      })
      .finally(() => setDbOnboardingChecked(true));
  }, [isSignedIn, clerkComplete, dbOnboardingChecked, getToken]);

  // Show loading state while checking auth
  if (!isLoaded) {
    /*
    // We previously had a spinner here, but removed it per request.
    if (location.pathname === "/onboarding") {
      return <div className="h-screen bg-background" />;
    }
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
      </div>
    );
    */
    // For now, return a blank background while loading to prevent flashes
    return <div className="h-screen bg-background" />;
  }

  // Redirect to landing if not signed in
  if (!isSignedIn) {
    return <Navigate to="/" replace />;
  }

  const onboardingComplete = clerkComplete || dbOnboardingComplete;

  // Still checking DB fallback — show spinner briefly
  if (!onboardingComplete && !dbOnboardingChecked) {
    /*
    // We previously had a spinner here, but removed it per request.
    if (location.pathname === "/onboarding") {
      return <>{children}</>;
    }
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
      </div>
    );
    */
    // For now, render the children immediately or a blank screen
    return location.pathname === "/onboarding" ? (
      <>{children}</>
    ) : (
      <div className="h-screen bg-background" />
    );
  }

  // Redirect to onboarding if not completed (unless already on /onboarding)
  if (!onboardingComplete && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
